/**
 * DOM Auto-Binder for Native / Custom Website Interfaces
 *
 * Allows developers to wire existing HTML buttons, inputs, and containers
 * to the Customers.new conversational AI engine with a single configuration call.
 */
import { HeadlessClient } from './headless.js';

export function bind(options = {}) {
    const client = new HeadlessClient(options);
    const elements = options.elements || {};

    const resolveEls = (selectorOrEl) => {
        if (!selectorOrEl) return [];
        if (typeof selectorOrEl === 'string') {
            return Array.from(document.querySelectorAll(selectorOrEl));
        }
        if (selectorOrEl instanceof HTMLElement) {
            return [selectorOrEl];
        }
        if (Array.isArray(selectorOrEl) || selectorOrEl instanceof NodeList) {
            return Array.from(selectorOrEl);
        }
        return [];
    };

    const inputEls = resolveEls(elements.input);
    const sendBtnEls = resolveEls(elements.sendButton);
    const voiceBtnEls = resolveEls(elements.voiceButton || elements.voiceTrigger || elements.micButton || elements.voiceOrb);
    const msgContainerEls = resolveEls(elements.messagesContainer || elements.messages);
    const welcomeScreenEls = resolveEls(elements.welcomeScreen);
    const convoScreenEls = resolveEls(elements.conversationScreen);
    const chipEls = resolveEls(elements.chips || elements.promptChips || '#promptChips .prompt-chip, .prompt-chip');

    // ── Input & Send Handlers ─────────────────────────

    const handleSend = (text) => {
        if (!text || !text.trim()) return;
        client.sendMessage(text.trim());
        inputEls.forEach(inp => { inp.value = ''; });

        // Switch to conversation screen if configured
        if (welcomeScreenEls.length > 0 && convoScreenEls.length > 0) {
            welcomeScreenEls.forEach(el => { el.style.display = 'none'; });
            convoScreenEls.forEach(el => { el.style.display = 'block'; });
            const parentCard = welcomeScreenEls[0]?.closest('#aiAssistantCard, .ai-assistant-card');
            if (parentCard) {
                parentCard.classList.remove('is-welcome');
                parentCard.classList.add('is-active');
            }
        }
    };

    sendBtnEls.forEach(btn => {
        btn.addEventListener('click', () => {
            const activeInput = inputEls.find(inp => inp.value.trim().length > 0) || inputEls[0];
            if (activeInput) handleSend(activeInput.value);
        });
    });

    inputEls.forEach(inp => {
        inp.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(inp.value);
            }
        });
    });

    chipEls.forEach(chip => {
        chip.addEventListener('click', () => {
            chipEls.forEach(c => c.classList.remove('active'));
            chip.classList.add('active');
            const promptText = chip.dataset.prompt || chip.textContent.replace(/^“|”$/g, '').trim();
            if (promptText) handleSend(promptText);
        });
    });

    // ── Voice Button / Orb Handlers ───────────────────

    voiceBtnEls.forEach(btn => {
        btn.addEventListener('click', async () => {
            // Switch to conversation view if needed
            if (welcomeScreenEls.length > 0 && convoScreenEls.length > 0) {
                welcomeScreenEls.forEach(el => { el.style.display = 'none'; });
                convoScreenEls.forEach(el => { el.style.display = 'block'; });
                const parentCard = welcomeScreenEls[0]?.closest('#aiAssistantCard, .ai-assistant-card');
                if (parentCard) {
                    parentCard.classList.remove('is-welcome');
                    parentCard.classList.add('is-active');
                }
            }
            await client.setMode('voice');
        });
    });

    // ── Message & Typing Rendering ────────────────────

    const getTimeString = () => {
        return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    // Inject pulse keyframes & default styles for bound chat elements if not present
    if (typeof document !== 'undefined' && !document.getElementById('igreet-binder-default-styles')) {
        const style = document.createElement('style');
        style.id = 'igreet-binder-default-styles';
        style.textContent = `
            @keyframes igreetPulse {
                0%, 100% { transform: scale(1); opacity: 0.4; }
                50% { transform: scale(1.4); opacity: 1; }
            }

            .chat-message {
                display: flex !important;
                gap: 8px !important;
                align-items: flex-start !important;
                margin-bottom: 12px !important;
                font-family: inherit !important;
                width: 100% !important;
                box-sizing: border-box !important;
            }

            .chat-message.user {
                flex-direction: row-reverse !important;
            }

            .chat-message .chat-content {
                display: flex !important;
                flex-direction: column !important;
                max-width: 82% !important;
            }

            .chat-message.user .chat-content {
                align-items: flex-end !important;
            }

            .chat-message .chat-bubble {
                padding: 9px 13px !important;
                border-radius: 12px !important;
                font-size: 12.5px !important;
                line-height: 1.45 !important;
                word-break: break-word !important;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
                box-sizing: border-box !important;
            }

            .chat-message.assistant .chat-bubble {
                background: #ffffff !important;
                color: #1e293b !important;
                border: 1px solid #e2e8f0 !important;
                border-top-left-radius: 4px !important;
            }

            .chat-message.user .chat-bubble {
                background: #004E61 !important;
                color: #ffffff !important;
                border: 1px solid transparent !important;
                border-top-right-radius: 4px !important;
            }

            .chat-message .chat-time {
                font-size: 10px !important;
                color: #94a3b8 !important;
                margin-top: 3px !important;
                padding: 0 2px !important;
            }

            .chat-avatar, .chat-avatar-btn {
                width: 28px !important;
                height: 28px !important;
                border-radius: 50% !important;
                background: #f1f5f9 !important;
                border: 1px solid #e2e8f0 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 14px !important;
                cursor: pointer !important;
                flex-shrink: 0 !important;
                padding: 0 !important;
                color: #004E61 !important;
                transition: all 0.15s ease !important;
                outline: none !important;
            }

            .chat-avatar-btn:hover {
                background: #e2e8f0 !important;
                transform: scale(1.05) !important;
            }
        `;
        document.head.appendChild(style);
    }

    const typingIndicators = new Map();

    const showTyping = (container) => {
        if (typingIndicators.has(container)) return;
        const msgRow = document.createElement('div');
        msgRow.className = 'chat-message assistant typing';
        
        const avatarBtn = document.createElement('div');
        avatarBtn.className = 'chat-avatar';
        avatarBtn.innerHTML = '🤖';
        msgRow.appendChild(avatarBtn);

        const contentDiv = document.createElement('div');
        contentDiv.className = 'chat-content';

        const bubble = document.createElement('div');
        bubble.className = 'chat-bubble';
        bubble.innerHTML = `
            <div class="typing-dots" style="display:inline-flex;align-items:center;gap:4px;padding:4px 6px;">
                <span style="width:6px;height:6px;background-color:currentColor;border-radius:50%;opacity:0.4;animation:igreetPulse 1.4s infinite both;"></span>
                <span style="width:6px;height:6px;background-color:currentColor;border-radius:50%;opacity:0.4;animation:igreetPulse 1.4s infinite both;animation-delay:0.2s;"></span>
                <span style="width:6px;height:6px;background-color:currentColor;border-radius:50%;opacity:0.4;animation:igreetPulse 1.4s infinite both;animation-delay:0.4s;"></span>
            </div>
        `;
        contentDiv.appendChild(bubble);

        const timeDiv = document.createElement('div');
        timeDiv.className = 'chat-time';
        timeDiv.textContent = getTimeString();
        contentDiv.appendChild(timeDiv);

        msgRow.appendChild(contentDiv);
        container.appendChild(msgRow);
        container.scrollTop = container.scrollHeight;
        typingIndicators.set(container, msgRow);
    };

    const hideTyping = (container) => {
        if (typingIndicators.has(container)) {
            const el = typingIndicators.get(container);
            el.remove();
            typingIndicators.delete(container);
        }
    };

    client.on('typing', (isTyping) => {
        msgContainerEls.forEach(container => {
            if (isTyping) {
                showTyping(container);
            } else {
                hideTyping(container);
            }
        });
    });

    const getBestNaturalVoice = () => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null;
        const voices = window.speechSynthesis.getVoices() || [];
        if (!voices.length) return null;

        const enVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('en'));
        const candidateList = enVoices.length ? enVoices : voices;

        const highQualityKeywords = [
            'natural', 'neural', 'premium', 'enhanced', 'siri',
            'ava', 'zoe', 'samantha', 'serena', 'oliver', 'kate', 'evan',
            'google us english', 'google uk english female', 'microsoft aria',
            'microsoft jenny', 'microsoft guy', 'microsoft christopher'
        ];

        let bestVoice = null;
        let highestScore = -999;

        for (const v of candidateList) {
            const nameLower = (v.name || '').toLowerCase();
            let score = 0;

            if (nameLower.includes('alex') || nameLower.includes('fred') || nameLower.includes('albert') || 
                nameLower.includes('bad news') || nameLower.includes('bells') || nameLower.includes('organ') ||
                nameLower.includes('cellos') || nameLower.includes('trinoids') || nameLower.includes('whisper')) {
                score -= 200;
            }

            for (const kw of highQualityKeywords) {
                if (nameLower.includes(kw)) score += 60;
            }

            if (nameLower.includes('natural') || nameLower.includes('neural')) score += 50;
            if (nameLower.includes('enhanced') || nameLower.includes('premium')) score += 35;
            if (nameLower.includes('online')) score += 20;
            if (v.lang === 'en-US' || v.lang === 'en_US' || v.lang === 'en-GB') score += 15;
            if (v.default) score += 5;

            if (score > highestScore) {
                highestScore = score;
                bestVoice = v;
            }
        }

        return bestVoice || candidateList[0] || null;
    };

    let currentAudio = null;
    let currentPlayingBtn = null;

    const fallbackWebSpeech = (cleanText, buttonEl, resetBtn) => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
            resetBtn(buttonEl);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.rate = 0.98;
        utterance.pitch = 1.0;
        const naturalVoice = getBestNaturalVoice();
        if (naturalVoice) utterance.voice = naturalVoice;

        utterance.onend = () => {
            resetBtn(buttonEl);
            if (currentPlayingBtn === buttonEl) currentPlayingBtn = null;
        };
        utterance.onerror = () => {
            resetBtn(buttonEl);
            if (currentPlayingBtn === buttonEl) currentPlayingBtn = null;
        };
        window.speechSynthesis.speak(utterance);
    };

    const resetTtsBtn = (btn) => {
        if (!btn) return;
        btn.classList.remove('is-playing');
        btn.innerHTML = `<span style="font-size:9px;">▶</span><span>Listen</span>`;
        btn.style.background = 'rgba(0, 78, 97, 0.08)';
        btn.style.color = '#004E61';
    };

    const speakTts = async (text, buttonEl, voice = 'aura-asteria-en') => {
        // If currently playing on this button, toggle pause
        if (currentAudio && !currentAudio.paused && currentPlayingBtn === buttonEl) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            resetTtsBtn(buttonEl);
            currentPlayingBtn = null;
            currentAudio = null;
            return;
        }

        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            if (currentPlayingBtn) resetTtsBtn(currentPlayingBtn);
        }
        if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
        }

        const cleanText = (text || '')
            .replace(/\[Image:\s*[^\]]+\]/gi, '')
            .replace(/!\[[^\]]*\]\([^)]+\)/gi, '')
            .replace(/<[^>]+>/g, '')
            .replace(/[*_#`~]/g, '')
            .trim();

        if (!cleanText) return;

        buttonEl.classList.add('is-playing');
        buttonEl.innerHTML = `<span style="font-size:9px;">⏹</span><span>Stop</span>`;
        buttonEl.style.background = '#004E61';
        buttonEl.style.color = '#ffffff';
        currentPlayingBtn = buttonEl;

        // 1. Try High-Definition Deepgram Aura Neural Audio
        try {
            const audioUrl = `/widget/tts?text=${encodeURIComponent(cleanText)}&voice=${encodeURIComponent(voice)}`;
            const audio = new Audio(audioUrl);
            currentAudio = audio;

            audio.onended = () => {
                resetTtsBtn(buttonEl);
                if (currentPlayingBtn === buttonEl) {
                    currentPlayingBtn = null;
                    currentAudio = null;
                }
            };

            audio.onerror = () => {
                console.warn('[iGreet] Neural audio stream error, falling back to browser speech');
                fallbackWebSpeech(cleanText, buttonEl, resetTtsBtn);
            };

            await audio.play();
        } catch (e) {
            console.warn('[iGreet] Neural TTS failed, fallback:', e);
            fallbackWebSpeech(cleanText, buttonEl, resetTtsBtn);
        }
    };

    client.on('message', (msg) => {
        msgContainerEls.forEach(container => {
            hideTyping(container);

            // Deduplicate identical sequential messages
            const lastMsgEl = container.querySelector('.chat-message:last-child .chat-bubble-text, .chat-message:last-child .chat-bubble');
            if (lastMsgEl && lastMsgEl.textContent.trim() === (msg.content || '').trim()) {
                return;
            }

            if (options.renderMessage) {
                options.renderMessage(msg, container);
            } else {
                // Default native bubble appending matching site structure
                const isAssistant = msg.role === 'assistant' || msg.role === 'bot';
                const msgRow = document.createElement('div');
                msgRow.className = `chat-message ${isAssistant ? 'assistant' : 'user'}`;
                
                if (isAssistant) {
                    const avatarBtn = document.createElement('button');
                    avatarBtn.className = 'chat-avatar-btn';
                    avatarBtn.setAttribute('type', 'button');
                    avatarBtn.setAttribute('aria-label', 'Assistant');
                    avatarBtn.innerHTML = `<span class="avatar-icon">🤖</span>`;
                    msgRow.appendChild(avatarBtn);
                }

                const contentDiv = document.createElement('div');
                contentDiv.className = 'chat-content';

                const bubble = document.createElement('div');
                bubble.className = 'chat-bubble';

                // Check for image citations
                let contentText = msg.content || '';
                const imageMatches = [...contentText.matchAll(/\[Image:\s*([^\]]+)\]/gi)];
                if (imageMatches.length > 0) {
                    imageMatches.forEach(m => {
                        const title = m[1].trim();
                        contentText = contentText.replaceAll(m[0], `<span class="image-reference-badge" style="display:inline-flex;align-items:center;gap:4px;background:rgba(107,69,228,0.1);color:#6b45e8;border-radius:4px;padding:1px 6px;font-size:0.75rem;font-weight:600;margin:0 2px;">🖼️ ${title}</span>`);
                    });
                }
                
                bubble.innerHTML = `<div class="chat-bubble-text">${contentText}</div>`;
                contentDiv.appendChild(bubble);

                const isTtsEnabled = client.config?.features?.ttsEnabled !== false && options.tts !== false;
                const timeRow = document.createElement('div');
                timeRow.style.cssText = 'display:flex; align-items:center; margin-top:3px;';

                const timeDiv = document.createElement('div');
                timeDiv.className = 'chat-time';
                timeDiv.textContent = getTimeString();
                timeRow.appendChild(timeDiv);

                if (isAssistant && isTtsEnabled) {
                    const ttsBtn = document.createElement('button');
                    ttsBtn.className = 'chat-tts-btn';
                    ttsBtn.type = 'button';
                    ttsBtn.title = 'Play message aloud';
                    ttsBtn.style.cssText = 'display: inline-flex; align-items: center; gap: 4px; background: rgba(0, 78, 97, 0.08); color: #004E61; border: 1px solid rgba(0, 78, 97, 0.2); border-radius: 12px; padding: 2px 7px; font-size: 10px; font-weight: 600; cursor: pointer; transition: all 0.15s ease; outline: none; margin-left: 6px;';
                    ttsBtn.innerHTML = `<span style="font-size:9px;">▶</span><span>Listen</span>`;
                    ttsBtn.onclick = (e) => {
                        e.stopPropagation();
                        speakTts(msg.content, ttsBtn);
                    };
                    timeRow.appendChild(ttsBtn);
                }

                contentDiv.appendChild(timeRow);

                msgRow.appendChild(contentDiv);
                container.appendChild(msgRow);
                container.scrollTop = container.scrollHeight;
            }
        });
    });

    // ── Image Card Rendering ──────────────────────────

    client.on('image', (imgData) => {
        msgContainerEls.forEach(container => {
            if (options.renderImageCard) {
                options.renderImageCard(imgData, container);
            } else {
                const targetBubble = container.querySelector('.chat-message.assistant:last-of-type .chat-bubble')
                    || container.querySelector('.chat-message:last-child .chat-bubble')
                    || container;

                const card = document.createElement('div');
                card.className = 'chat-referenced-image-card';
                card.style.cssText = 'margin-top:8px;border-radius:8px;overflow:hidden;border:1px solid rgba(107,69,228,0.16);background:rgba(255,255,255,0.85);padding:5px;box-shadow:0 1px 4px rgba(0,0,0,0.06);width:100%;max-width:280px;box-sizing:border-box;cursor:pointer;transition:transform 0.15s ease;';
                card.onmouseenter = () => { card.style.transform = 'scale(1.015)'; };
                card.onmouseleave = () => { card.style.transform = 'scale(1)'; };
                card.onclick = () => { window.open(imgData.url, '_blank'); };

                const imgEl = document.createElement('img');
                imgEl.src = imgData.url;
                imgEl.alt = imgData.title || 'Referenced Image';
                imgEl.style.cssText = 'width:100%;height:auto;max-height:220px;object-fit:contain;background:#f8fafc;border-radius:6px;display:block;';
                imgEl.onload = () => {
                    container.scrollTop = container.scrollHeight;
                };

                card.appendChild(imgEl);

                if (imgData.title) {
                    const titleEl = document.createElement('div');
                    titleEl.style.cssText = 'font-size:0.75rem;font-weight:600;color:#2c2436;margin-top:5px;padding:0 3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
                    titleEl.textContent = imgData.title;
                    card.appendChild(titleEl);
                }

                if (imgData.description && imgData.description.trim() !== (imgData.title || '').trim()) {
                    const descEl = document.createElement('div');
                    descEl.style.cssText = 'font-size:0.7rem;color:#6b7280;margin-top:2px;padding:0 3px;line-height:1.3;';
                    descEl.textContent = imgData.description;
                    card.appendChild(descEl);
                }

                targetBubble.appendChild(card);
                container.scrollTop = container.scrollHeight;
            }
        });
    });

    // Automatically initialize client
    client.init();

    return client;
}

/**
 * Inbound Form Auto-Binder
 *
 * Connects any HTML <form> directly to customers.new inbound ingestion engine
 * with automatic validation, visitor tracking, and CRM synchronization.
 */
export function bindForm(options = {}) {
    const server = options.server || (typeof window !== 'undefined' ? window.location.origin : '');
    const formId = options.formId;
    if (!formId) {
        console.error('[iGreetWidget] bindForm requires a formId option.');
        return null;
    }

    const forms = [];
    if (options.selector || options.element || options.form) {
        const target = options.selector || options.element || options.form;
        if (typeof target === 'string') {
            forms.push(...document.querySelectorAll(target));
        } else if (target instanceof HTMLElement) {
            forms.push(target);
        }
    } else {
        forms.push(...document.querySelectorAll(`[data-igreet-form="${formId}"], [data-form-id="${formId}"]`));
    }

    if (forms.length === 0) {
        return null;
    }

    forms.forEach(form => {
        if (form._igreetBound) return;
        form._igreetBound = true;

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
            const statusEl = options.statusSelector
                ? document.querySelector(options.statusSelector)
                : form.querySelector('.form-status, [data-form-status], [data-eval-status], .modal-status');

            if (submitBtn) submitBtn.disabled = true;
            if (statusEl) {
                statusEl.innerHTML = '<div style="color: #6b7280; font-size: 0.85rem;">Submitting your request...</div>';
            }

            const formData = new FormData(form);
            const payload = {};
            formData.forEach((value, key) => {
                payload[key] = value;
            });

            // Include visitor tracking ID if available
            let visitorId = null;
            try {
                visitorId = localStorage.getItem('polished_visitor_id') || localStorage.getItem('igreet_visitor_id') || null;
            } catch {}

            payload.sourcePage = window.location.pathname || '/';
            if (visitorId) payload.visitorId = visitorId;

            try {
                const res = await fetch(`${server}/api/inbound/forms/${encodeURIComponent(formId)}/submit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                    if (statusEl) {
                        statusEl.innerHTML = `<div style="color: #059669; font-size: 0.85rem; font-weight: 500;">${data.message || 'Thank you! Your submission has been received.'}</div>`;
                    }
                    form.reset();
                    if (typeof options.onSuccess === 'function') options.onSuccess(data);
                    if (data.redirectUrl) {
                        setTimeout(() => { window.location.href = data.redirectUrl; }, 1200);
                    }
                } else {
                    const errMsg = data.message || data.error || 'There was an error submitting your request. Please try again.';
                    if (statusEl) {
                        statusEl.innerHTML = `<div style="color: #dc2626; font-size: 0.85rem;">${errMsg}</div>`;
                    }
                    if (typeof options.onError === 'function') options.onError(errMsg);
                }
            } catch (err) {
                const errMsg = 'Network error. Please try again in a few moments.';
                if (statusEl) {
                    statusEl.innerHTML = `<div style="color: #dc2626; font-size: 0.85rem;">${errMsg}</div>`;
                }
                if (typeof options.onError === 'function') options.onError(err);
            } finally {
                if (submitBtn) submitBtn.disabled = false;
            }
        });
    });

    return forms;
}
